<template>
    <div class="page">
        <!-- Sticky Header -->
        <header class="header">
            <div class="container">

                <ToastManagerVue ref="toastManager" />

                <button class="back-btn" @click="handleBack">
                    ← Back
                </button>

                <h1>
                    {{ currentStep === 1 ? "Register on Behalf" : "Review Your Information" }}
                </h1>

                <!-- Progress -->
                <div class="progress">
                    <div class="step">
                        <span class="circle">1</span>
                        <span class="label">Step 1</span>
                    </div>

                    <div class="line" :class="{ 'line-active': currentStep === 2 }"></div>

                    <div class="step">
                        <span class="circle" :class="{ inactive: currentStep === 1 }">2</span>
                        <span class="label" :class="{ muted: currentStep === 1 }">Step 2</span>
                    </div>
                </div>
            </div>
        </header>

        <!-- Content -->
        <main class="content">
            <div class="container">
                <!-- STEP 1 -->
                <form v-if="currentStep === 1" class="form" @submit.prevent="submitForm">
                    <!-- User Info -->
                    <div class="title-with-icon">
                        <svg class="icon" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" fill="none"
                            stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="8" r="5"></circle>
                            <path d="M20 21a8 8 0 0 0-16 0"></path>
                        </svg>

                        <h3>Your Information</h3>

                    </div>

                    <div class="field">
                        <label>Your Full Name <span>*</span></label>
                        <input v-model="form.full_name" type="text" placeholder="Enter your full name" />
                        <small v-if="errors.userFullName" class="error"><span class="error-icon">⚠</span>{{
                            errors.userFullName
                        }}</small>
                    </div>

                    <div class="field">
                        <label>Your Phone Number <span>*</span></label>
                        <div class="phone-input">
                            <span>+234</span>
                            <input v-model="form.phone_number" type="tel" maxlength="10" placeholder="8012345678" />
                        </div>
                        <p class="note">We'll use this to contact you about the registration</p>
                        <small v-if="errors.userPhone" class="error"><span class="error-icon">⚠</span>{{
                            errors.userPhone }}</small>
                    </div>

                    <!-- Participant Info -->
                    <div class="title-with-icon">
                        <svg class="icon" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" fill="none"
                            stroke-linecap="round" stroke-linejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <h3>Participant Information</h3>
                    </div>
                    <div class="field">
                        <label>Participant's Full Name <span>*</span></label>
                        <input v-model="form.participant_full_name" type="text" placeholder="Enter participant name" />
                        <small v-if="errors.participantFullName" class="error"><span class="error-icon">⚠</span>{{
                            errors.participantFullName }}</small>
                    </div>

                    <div class="field">
                        <label>Participant's Date of Birth <span>*</span></label>

                        <input v-model="form.participant_dob" type="date" :max="maxDob"
                            @change="updateParticipantAge" />

                        <small v-if="participantAge !== null"
                            :class="['age-preview', participantAge < 18 ? 'age-error' : 'age-ok']">
                            Age: {{ participantAge }} years
                        </small>

                        <small v-if="errors.participantDob" class="error">
                            ⚠ {{ errors.participantDob }}
                        </small>
                    </div>
                    <!-- Home Address -->
                    <div class="field">
                        <label>Home Address (Include LGA) <span>*</span></label>
                        <input v-model="form.participant_address" type="text"
                            placeholder="Enter full home address with LGA" />
                        <small v-if="errors.participantAddress" class="error">⚠ {{ errors.participantAddress }}</small>
                    </div>

                    <!-- Highest Education Level -->
                    <div class="field">
                        <label>Highest Education Level <span>*</span></label>
                        <select v-model="form.participant_education_level">
                            <option value="">Select education level</option>
                            <option>Secondary School</option>
                            <option>BSc</option>
                            <option>Nursing Degree</option>
                            <option>HND</option>
                            <option>ND</option>
                            <option>Others</option>
                        </select>
                        <small v-if="errors.participantEducation" class="error">⚠ {{ errors.participantEducation
                        }}</small>
                    </div>

                    <!-- Certificate Upload -->
                    <div class="field">
                        <label>Upload Highest Certificate / Credentials <span></span></label>
                        <input type="file" accept="image/*,application/pdf"
                            @change="handleParticipantCertificateUpload" />
                        <small v-if="participantCertificateName" class="file-name">📄 {{ participantCertificateName
                        }}</small>
                        <small v-if="errors.participantCertificate" class="error">⚠ {{ errors.participantCertificate
                        }}</small>
                    </div>

                    <!-- Profile Photo -->
                    <div class="field">
                        <label>Upload Recent Profile Photograph <span></span></label>
                        <input type="file" accept="image/*" @change="handleParticipantPhotoUpload" />
                        <img v-if="participantPhotoPreview" :src="participantPhotoPreview" class="photo-preview" />
                        <small v-if="errors.participantPhoto" class="error">⚠ {{ errors.participantPhoto }}</small>
                    </div>

                    <div class="field">
                        <label>Participant's State of Origin <span>*</span></label>
                        <select v-model="form.participant_state">
                            <option value="">Select your state</option>
                            <option v-for="state in states" :key="state.alias" :value="state.state">
                                {{ state.state }}
                            </option>
                        </select>
                        <small v-if="errors.participantState" class="error"><span class="error-icon">⚠</span>{{
                            errors.participantState }}</small>
                    </div>

                    <div class="field">
                        <label>Participant's LGA of Origin<span>*</span></label>
                        <select v-model="form.participant_lga" :disabled="!form.participant_state">
                            <option value="">
                                {{ form.participant_state ? "Select LGA" : "Please select a state first" }}
                            </option>
                            <option v-for="lga in lgas" :key="lga" :value="lga">
                                {{ lga }}
                            </option>
                        </select>
                        <small v-if="errors.participantLGA" class="error"><span class="error-icon">⚠</span>{{
                            errors.participantLGA
                        }}</small>
                    </div>

                    <div class="field">
                        <label>Participant's Phone Number <span>*</span></label>
                        <div class="phone-input">
                            <span>+234</span>
                            <input v-model="form.participant_phone_number" type="tel" maxlength="10"
                                placeholder="8012345678" />
                        </div>
                        <small v-if="errors.participantPhone" class="error"><span class="error-icon">⚠</span>{{
                            errors.participantPhone }}</small>

                    </div>

                    <div class="field">
                        <label>Particpant's WhatsApp <span>*</span></label>
                        <div class="phone-input">
                            <span>+234</span>
                            <input v-model="form.participant_whatsapp_number" type="tel" maxlength="10"
                                placeholder="8012345678" />
                        </div>
                        <small v-if="errors.participantWhatsapp" class="error"><span class="error-icon">⚠</span>{{
                            errors.participantWhatsapp }}</small>
                    </div>

                    <div class="checkbox">
                        <input type="checkbox" id="sameAsPhone" v-model="sameAsPhone" @change="copyPhone" />
                        <label for="sameAsPhone">WhatsApp same as phone</label>
                    </div>

                    <button class="submit" :disabled="participantAge === null || participantAge < 18
                        ">
                        Continue
                    </button>


                    <div class="help">
                        <button type="button">Need help?</button>
                    </div>

                </form>

                <!-- STEP 2 -->
                <div v-if="currentStep === 2">
                    <div class="form">
                        <div class="group-form-edit">
                            <h3>Review Your Information</h3>
                            <button class="edit-btn" @click="currentStep = 1">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="pen-icon">
                                    <path
                                        d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z">
                                    </path>
                                </svg>
                                Edit
                            </button>

                        </div>

                        <div class="review">
                            <span>Your Full Name</span><span>{{ form.full_name }}</span>
                            <span>Your Phone Number</span><span>+234{{ form.phone_number }}</span>

                            <span>Participant's Full Name</span><span>{{ form.participant_full_name }}</span>
                            <span>Participant's State</span><span>{{ form.participant_state }}</span>
                            <span>Participant's LGA</span><span>{{ form.participant_lga }}</span>
                            <span>Participant's Phone Number</span><span>+234{{ form.participant_phone_number }}</span>
                            <span>Participant's WhatsApp</span><span>+234{{ form.participant_whatsapp_number }}</span>
                            <!-- <span>Training Location</span>
                            <span>{{ form.training_location }}</span> -->
                        </div>
                    </div>

                    <div class="form">
                        <div class="checkbox">
                            <input type="checkbox" v-model="termsAccepted" />
                            <label>
                                I agree to the <router-link :to="{ name: 'CGTerms' }" class="link">
                                    terms and conditions
                                </router-link> and confirm that the information provided is
                                accurate
                            </label>
                        </div>
                        <small v-if="errors.terms" class="error"><span class="error-icon">⚠</span>{{ errors.terms
                        }}</small>
                    </div>

                    <button class="submit" @click="finalSubmit">Complete Registration</button>
                    <button class="custom-btn" @click="currentStep = 1">Go Back</button>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import statesData from "@/data/states.js";
import Request from "@/utils/apiclient.js";
import { formatErrorMessages } from "@/utils/errorHandler";
import ToastManagerVue from "@/components/ToastAlert/ToastManager.vue";

export default {
    name: "BehalfRegistration",
    components: {
        ToastManagerVue
    },
    data() {
        return {
            currentStep: 1,
            sameAsPhone: false,
            termsAccepted: false,
            maxDob: "",
            participantAge: null, // 👈 NEW

            certificateName: null,
            participantCertificateName: null, // 👈 NEW
            participantPhotoPreview: null,
            form: {
                full_name: "",
                phone_number: "",
                participant_full_name: "",
                participant_state: "",
                participant_lga: "",
                participant_phone_number: "",
                participant_whatsapp_number: "",
                participant_dob: "",
                participant_address: "",
                participant_education_level: "",
                participant_certificate: null,
                participant_photo: null,              // 👈 NEW
            },
            isSubmitting: false,
            states: statesData.states,
            lgas: [],
            errors: {},
        };
    },
    watch: {
        "form.participant_state"(newState) {
            this.form.participant_lga = "";
            const selected = this.states.find(s => s.state === newState);
            this.lgas = selected ? selected.lgas : [];
        },
    },

    mounted() {
        const today = new Date();
        const year = today.getFullYear() - 18;
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        this.maxDob = `${year}-${month}-${day}`;
    },


    methods: {
        showToast(msg, statusValue) {
            this.$refs.toastManager.addToast(msg, statusValue);
            return true;
        },
        handleBack() {
            if (this.currentStep === 1) {
                this.$router.push({ name: 'CgWorkshop' });
            } else {
                this.currentStep = 1;
            }
        },
        copyPhone() {
            if (this.sameAsPhone) {
                this.form.participant_whatsapp_number = this.form.participant_phone_number;
            }
        },

        updateParticipantAge() {
            if (!this.form.participant_dob) {
                this.participantAge = null;
                return;
            }

            const dob = new Date(this.form.participant_dob);
            const today = new Date();
            let age = today.getFullYear() - dob.getFullYear();
            const m = today.getMonth() - dob.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;

            this.participantAge = age;

            if (age < 18) {
                this.errors.participantDob = "Participant must be at least 18 years old";
            } else {
                this.errors.participantDob = null;
            }
        },

        handleParticipantCertificateUpload(e) {
            const file = e.target.files[0];
            if (!file) return;

            const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
            const maxSize = 10 * 1024 * 1024;

            if (!allowedTypes.includes(file.type)) {
                this.errors.participantCertificate = "Only PDF or image files allowed";
                return;
            }

            if (file.size > maxSize) {
                this.errors.participantCertificate = "File must not exceed 10MB";
                return;
            }

            this.form.participant_certificate = file;
            this.participantCertificateName = file.name;
            this.errors.participantCertificate = null;
        },

        handleParticipantPhotoUpload(e) {
            const file = e.target.files[0];
            if (!file) return;

            const maxSize = 10 * 1024 * 1024;

            if (!file.type.startsWith("image/")) {
                this.errors.participantPhoto = "Only image files allowed";
                return;
            }

            if (file.size > maxSize) {
                this.errors.participantPhoto = "Photo must not exceed 10MB";
                return;
            }

            this.form.participant_photo = file;
            this.participantPhotoPreview = URL.createObjectURL(file);
            this.errors.participantPhoto = null;
        },

        submitForm() {
            this.errors = {};
            // Basic validation
            if (!this.form.full_name) this.errors.userFullName = "Your Full name is Required";
            if (!this.form.phone_number) this.errors.userPhone = "Your Phone Number is Required";
            if (!this.form.participant_full_name) this.errors.participantFullName = "Participant's full name is Required";
            if (!this.form.participant_state) this.errors.participantState = "Participant's State is Required";
            if (!this.form.participant_lga) this.errors.participantLGA = "Participant's LGA Required";
            if (!this.form.participant_phone_number) this.errors.participantPhone = "Participant's Phone Number is Required";
            if (!this.form.participant_whatsapp_number) this.errors.participantWhatsapp = "Participant's Whatsapp is Required";

            if (Object.keys(this.errors).length === 0) {
                this.currentStep = 2;
            }
        },
        finalSubmit() {
            this.errors = {};
            if (!this.termsAccepted) {
                this.errors.terms = "You must accept the terms";
                return;
            }

            // Clone the form and prepend +234 to phone numbers
            const payload = {
                ...this.form,
                registration_type: "behalf_registration",
                phone_number: "+234" + this.form.phone_number,
                participant_phone_number: "+234" + this.form.participant_phone_number,
                participant_whatsapp_number: "+234" + this.form.participant_whatsapp_number,
            };

             const formData = new FormData();

            for (const key in payload) {
                if (payload[key] !== null && payload[key] !== "") {
                    formData.append(key, payload[key]);
                }
            }

            console.log([...formData.entries()]);

            const request = new Request("/workshop/registrations/");
            request.postFormData(formData, (data, error) => {
                this.isSubmitting = true;
                console.log(formData);
                if (error) {
                    this.isSubmitting = false;
                    console.log("data error", error);
                    if (error.status == "failure") {
                        return this.showToast(error.message, "error");
                    }
                    if (error.detail) {
                        return this.showToast("An error occurred", "error");
                    }
                    const errorMessages = formatErrorMessages(error);
                    console.log("error", errorMessages);
                    errorMessages.forEach((message) => this.showToast(message, "error"));
                } else {
                    console.log("data success here", data);
                    this.currentStep = 1;
                    this.form = {
                        full_name: "",
                        phone_number: "",
                        participant_full_name: "",
                        participant_state: "",
                        participant_lga: "",
                        participant_phone_number: "",
                        participant_whatsapp_number: "",
                    };

                    this.isSubmitting = false;

                    this.$router.push({
                        name: "CgWorkshopConfirm",
                        state: {
                            registrationType: data.registration_type,
                            registrationNumber: data.registration_number,
                            participantName: data.participant_full_name,
                            registrationDate: data.created_at,
                        },
                    });
                }
            });
        },
    },
};
</script>

<style scoped>
/* Base styling same as your pattern */
.page {
    min-height: 100vh;
    background: #f8f9fa;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    color: #212529;
}

.container {
    max-width: 640px;
    margin: auto;
    padding: 0 16px;
}

.header {
    background: #ffffff;
    border-bottom: 1px solid #ced4da;
    position: sticky;
    top: 0;
    z-index: 10;
    padding: 16px 0 12px;
}

.header h1 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 12px;
}

.back-btn {
    background: none;
    border: none;
    color: #f59cab;
    cursor: pointer;
    font-size: 0.9rem;
    padding: 0;
    margin: 10px 0;
}

.progress {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
}

.step {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 20px 0 20px;
}

.circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f59cab;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
}

.circle.inactive {
    background: #e9ecef;
    color: #6c757d;
}

.line {
    width: 48px;
    height: 2px;
    background: #ced4da;
}

.line-active {
    background: #f59cab;
}

.muted {
    color: #6c757d;
}

.content {
    padding: 28px 0;
}

.form {
    background: #fff;
    padding: 28px;
    border-radius: 18px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
    margin-bottom: 20px;
}

.field {
    margin-bottom: 20px;
}

label {
    font-size: 0.85rem;
    font-weight: 500;
}

label span {
    color: #dc3545;
}

input,
select {
    width: 100%;
    height: 42px;
    margin-top: 6px;
    border-radius: 8px;
    border: 1px solid #ced4da;
    padding: 0 14px;
    font-size: 0.9rem;
}

input:focus {
    outline: none;
    border-color: #f59cab;
    box-shadow: 0 0 0 3px rgba(245, 156, 171, 0.15);
}

.phone-input {
    position: relative;
}

.phone-input span {
    position: absolute;
    left: 14px;
    top: 55%;
    transform: translateY(-50%);
    font-size: 0.85rem;
    color: #6c757d;
}

.phone-input input {
    padding-left: 56px;
}

.checkbox {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 14px;
    font-size: 0.85rem;
}

input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #f59cab;
    cursor: pointer;
}

.review {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 10px 8px;
    margin-bottom: 20px;
}

.review span:nth-child(odd) {
    color: #6c757d;
}

.submit {
    width: 100%;
    height: 48px;
    background: #f59cab;
    color: #fff;
    border: none;
    border-radius: 10px;
    margin-top: 18px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
}

.submit:hover {
    opacity: 0.95;
}

a {
    color: #f59cab;
}

.custom-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 48px;
    padding: 0 16px;
    font-size: 0.9rem;
    font-weight: 500;
    border-radius: 8px;
    border: 1px solid #f59cab;
    background-color: #ffffff;
    color: #f59cab;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    text-align: center;
    margin-top: 20px;
}

.group-form-edit {
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
}



.error {
    display: flex;
    align-items: center;
    gap: 6px;
    /* space between icon and text */
    margin-top: 6px;
    color: #dc3545;
    /* red color for error */
    font-size: 0.75rem;
}

.error-icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    text-align: center;
    line-height: 16px;
    flex-shrink: 0;
}

.title-with-icon {
    display: flex;
    align-items: center;
    gap: 8px;
    /* space between icon and text */
    margin-bottom: 10px;
    color: #f59cab;
    border-bottom: 1px solid #ced4da;

}

.title-with-icon h3 {
    font-weight: 500;
    margin-bottom: 10px;
}

.title-with-icon .icon {
    width: 24px;
    height: 24px;
    stroke: #f59cab;
    margin-bottom: 10px;
    /* optional: set icon color */
}

.note {
    font-size: 14px;
    color: #6C757D;
    margin-top: 5px;
}

/* Help */
.help {
    display: flex;
    justify-content: center;
    margin-top: 22px;
}

.help button {
    background: none;
    border: none;
    color: #f59cab;
    font-size: 0.85rem;
    cursor: pointer;
}

.help button:hover {
    text-decoration: underline;
}

.edit-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    /* space between icon and text */
    padding: 4px 8px;
    background: none;
    border: none;
    color: #f59cab;
    /* primary color */
    cursor: pointer;
    font-size: 0.85rem;
}

.edit-btn:hover {
    text-decoration: underline;
}

.pen-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

.radio-group {
    display: flex;
    gap: 18px;
    margin-top: 8px;
}

.radio-option {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 0.85rem;
}

/* hide native radio */
.radio-option input[type="radio"] {
    display: none;
}

/* custom small circle */
.custom-radio {
    width: 14px;
    height: 14px;
    border: 2px solid #ced4da;
    border-radius: 50%;
    position: relative;
}

/* inner dot */
.radio-option input[type="radio"]:checked+.custom-radio::after {
    content: "";
    width: 6px;
    height: 6px;
    background: #f59cab;
    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0 0 0 3px rgba(245, 156, 171, 0.15);
}

.radio-text {
    color: #212529;
}

.age-preview {
    display: block;
    margin-top: 6px;
    font-size: 0.75rem;
}

.age-ok {
    color: #198754;
}

.age-error {
    color: #dc3545;
}

.file-name {
    font-size: 0.75rem;
    margin-top: 4px;
    display: block;
}

.submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.photo-preview {
    width: 100px;
    margin-top: 8px;
    border-radius: 6px;
}
</style>
